import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fires a Sonner toast on login if the user's paid subscription is expiring
// within 14 days (or has already expired). Skipped for free-tier users and
// for lifetime grants (premium_plus with no expiry).
function maybeWarnAboutExpiry(userData) {
  if (!userData) return;
  const tier = userData.subscription_tier;
  const expires = userData.subscription_expires;
  if (!expires || tier === 'free') return;

  const expiryDate = new Date(expires);
  if (isNaN(expiryDate.getTime())) return;
  const daysLeft = Math.floor((expiryDate - new Date()) / (1000 * 60 * 60 * 24));

  if (daysLeft < 0) {
    toast.error('Premium expired — renew to keep Levels 5–17', {
      action: { label: 'Renew', onClick: () => { window.location.href = '/subscription'; } },
      duration: 8000,
    });
  } else if (daysLeft === 0) {
    toast.warning('Premium expires today — renew now', {
      action: { label: 'Renew', onClick: () => { window.location.href = '/subscription'; } },
      duration: 8000,
    });
  } else if (daysLeft <= 14) {
    toast.warning(`Premium expires in ${daysLeft} day${daysLeft === 1 ? '' : 's'}`, {
      action: { label: 'Renew', onClick: () => { window.location.href = '/subscription'; } },
      duration: 6000,
    });
  }
}

const AuthContext = createContext(null);

// Global axios 401 interceptor: when any authenticated request gets a 401
// (typically because the 4-hour access-token cookie expired while the tab
// was backgrounded), transparently POST /auth/refresh and retry the
// original request ONCE. This prevents the "stale React state thinks
// you're logged in, cookie says otherwise" pattern from showing up on
// the Admin page, Subscription page, promo redemption, etc.
//
// Registered at module load so it's active for the lifetime of the SPA.
let _refreshPromise = null; // de-dupe concurrent refreshes
axios.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    if (
      status === 401 &&
      original &&
      !original._retried &&
      !/\/api\/auth\/(login|refresh|me|logout|register)/.test(original.url || '')
    ) {
      original._retried = true;
      try {
        if (!_refreshPromise) {
          _refreshPromise = axios
            .post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true })
            .finally(() => { _refreshPromise = null; });
        }
        await _refreshPromise;
        return axios(original);
      } catch {
        // Refresh failed — let the original 401 bubble up; the caller
        // (or Admin/Subscription page) will route to /login with a toast.
      }
    }
    return Promise.reject(error);
  },
);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const formatApiErrorDetail = useCallback((detail) => {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
      return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Auto-refresh access token every 30 minutes while logged in
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(async () => {
      try {
        await axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true });
      } catch {
        // Refresh failed — token may be expired, re-check auth
        checkAuth();
      }
    }, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user, checkAuth]);

  const login = useCallback(async (email, password, rememberMe = false) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/login`,
        { email, password, remember_me: rememberMe },
        { withCredentials: true }
      );
      setUser(data);
      maybeWarnAboutExpiry(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, [formatApiErrorDetail]);

  const register = useCallback(async (email, password, name) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/register`,
        { email, password, name },
        { withCredentials: true }
      );
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, [formatApiErrorDetail]);

  const registerWithUsername = useCallback(async (username, password) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/register-username`,
        { username, password },
        { withCredentials: true }
      );
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, [formatApiErrorDetail]);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } finally {
      setUser(false);
    }
  }, []);

  const refreshAuth = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true });
      await checkAuth();
    } catch {
      setUser(false);
    }
  }, [checkAuth]);

  const contextValue = useMemo(() => ({
    user,
    setUser,
    loading,
    login,
    register,
    registerWithUsername,
    logout,
    refreshAuth,
    checkAuth
  }), [user, loading, login, register, registerWithUsername, logout, refreshAuth, checkAuth]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
